import { Eye, FileText, Globe, Loader2, Trash2 } from 'lucide-react';

function formatDate(dateValue) {
  return new Date(dateValue).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStructureLabel(document) {
  if (document.chunking_strategy === 'chapter') {
    return 'Chapitres';
  }
  if (document.chapter_candidate) {
    return 'PDF long';
  }
  return 'Pages';
}

function getStructureBadgeClass(document) {
  if (document.chunking_strategy === 'chapter') {
    return 'bg-emerald-900/30 text-emerald-400';
  }
  if (document.chapter_candidate) {
    return 'bg-amber-900/30 text-amber-400';
  }
  return 'bg-zinc-800 text-zinc-400';
}

function SourceTypeIcon({ sourceType }) {
  return sourceType === 'pdf'
    ? <FileText className="h-4 w-4 text-blue-400" />
    : <Globe className="h-4 w-4 text-green-400" />;
}

function DocumentBadges({ document }) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <SourceTypeIcon sourceType={document.source_type} />
      <span className="text-xs font-mono uppercase text-zinc-500">{document.source_type}</span>
      {document.page_count ? (
        <span className="rounded bg-zinc-800 px-2 py-0.5 text-[11px] uppercase tracking-wide text-zinc-300">
          {document.page_count} pages
        </span>
      ) : null}
      {document.source_type === 'pdf' ? (
        <span className={`rounded px-2 py-0.5 text-[11px] uppercase tracking-wide ${getStructureBadgeClass(document)}`}>
          {getStructureLabel(document)}
        </span>
      ) : null}
      {document.section_count ? (
        <span className="rounded bg-zinc-800 px-2 py-0.5 text-[11px] uppercase tracking-wide text-zinc-400">
          {document.section_count} sections
        </span>
      ) : null}
    </div>
  );
}

function DocumentSourceLink({ sourceUrl }) {
  if (!sourceUrl) {
    return null;
  }

  return (
    <a
      href={sourceUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block truncate text-xs text-zinc-400 transition-colors hover:text-white"
    >
      {sourceUrl}
    </a>
  );
}

function DocumentMeta({ document }) {
  return (
    <div className="mt-2 text-xs text-muted-foreground">
      {document.chunk_count} chunk{document.chunk_count > 1 ? 's' : ''}
      {' • '}
      {formatDate(document.created_at)}
      {document.reingested_at ? ` • Réingéré` : ''}
    </div>
  );
}

function DocumentViewButton({ documentId, onViewClick, document }) {
  return (
    <button
      type="button"
      data-testid={`documents-view-button-${documentId}`}
      onClick={() => onViewClick(document)}
      className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
      title="Visualiser le PDF"
    >
      <Eye className="h-4 w-4" />
    </button>
  );
}

function DocumentDeleteButton({ documentId, isDeleting, onDeleteClick, document }) {
  return (
    <button
      type="button"
      data-testid={`documents-delete-button-${documentId}`}
      onClick={() => onDeleteClick(document)}
      disabled={isDeleting}
      className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-900/20 hover:text-red-400 disabled:opacity-50"
      title="Supprimer"
    >
      {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
    </button>
  );
}

export function DocumentListItem({ document, deleting, onDeleteClick, onViewClick }) {
  const isDeleting = deleting === document.id;
  const isPdf = document.source_type === 'pdf';

  return (
    <div
      data-testid={`documents-item-${document.id}`}
      className="rounded-sm border border-zinc-800 bg-zinc-900/30 p-4 transition-colors hover:border-zinc-700"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <DocumentBadges document={document} />
          <h3 className="mb-1 truncate font-medium">{document.title}</h3>
          <DocumentSourceLink sourceUrl={document.source_url} />
          <DocumentMeta document={document} />
        </div>
        <div className="flex items-start gap-1">
          {isPdf && onViewClick ? (
            <DocumentViewButton
              documentId={document.id}
              onViewClick={onViewClick}
              document={document}
            />
          ) : null}
          <DocumentDeleteButton
            documentId={document.id}
            isDeleting={isDeleting}
            onDeleteClick={onDeleteClick}
            document={document}
          />
        </div>
      </div>
    </div>
  );
}
